<template>
  <div>
    <div
      v-for="(contact, index) in contactsWithParsedData"
      :key="index"
      class="contact--share"
    >
      <div class="contact--container">
        <div class="contact--data">
          <p>
            <span>{{ contact.displayName }}</span>
          </p>
          <template v-if="contact.phoneNumbers.length">
            <div
              v-for="(phone, phoneIndex) in contact.phoneNumbers"
              :key="phoneIndex"
              class="contact--wrap"
            >
              <div class="contact--phone">{{ phone.number }}</div>
            </div>
          </template>
        </div>
        <div class="contact--avatar">
          <div class="contact--avatar-container">
            <fluent-icon icon="person" class="fluent-icon" size="20px" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VCardParser from 'vcard-parser';

export default {
  props: {
    contentAttributes: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  computed: {
    /**
     * Obtiene la lista de contactos desde contentAttributes
     */
    contacts() {
      return (
        this.contentAttributes.contacts || [
          {
            contact_name:
              this.contentAttributes.contact_name ||
              this.contentAttributes.content,
            vcard: this.contentAttributes.vcard,
          },
        ]
      );
    },

    /**
     * Procesa cada contacto y parsea su vCard individual
     */
    contactsWithParsedData() {
      return this.contacts.map(contact => {
        const parsedVCard = this.parseVCard(contact.vcard);

        // WhatsApp Cloud entrega los teléfonos explícitos en `phones` (sin vCard);
        // EvolutionAPI los trae dentro del vCard, así que se mantiene su parseo.
        const phoneNumbers = contact.phones
          ? this.extractPhonesFromList(contact.phones)
          : this.extractPhoneNumbers(parsedVCard);

        return {
          ...contact,
          parsedVCard: parsedVCard,
          displayName: this.getDisplayName(contact, parsedVCard),
          // Si el contacto se compartió sin teléfono, se muestra un texto localizado en el
          // lugar del número (la entrada no se formatea para no perder el texto).
          phoneNumbers: phoneNumbers.length
            ? phoneNumbers
            : [
                {
                  number: this.$t(
                    'CONVERSATION.SHARED_CONTACT_PHONE_UNAVAILABLE'
                  ),
                  raw: '',
                },
              ],
        };
      });
    },
  },

  methods: {
    /**
     * Parsea un vCard individual
     */
    parseVCard(vcard) {
      if (!vcard) return null;

      try {
        // Reemplazar \n con saltos de línea reales
        const vcardString = vcard.replace(/\\n/g, '\n');
        const parsed = VCardParser.parse(vcardString);
        return parsed;
      } catch (error) {
        console.error('Error parsing vCard:', error);
        return null;
      }
    },

    /**
     * Obtiene el nombre a mostrar para un contacto
     */
    getDisplayName(contact, parsedVCard) {
      // Prioridad: contact_name > nombre del vCard > fallback
      if (contact.contact_name) return contact.contact_name;

      if (parsedVCard) {
        // Intentar obtener el nombre completo (FN)
        if (parsedVCard.fn && parsedVCard.fn.value) {
          return parsedVCard.fn.value;
        }

        // Si no hay FN, construir desde N (nombre estructurado)
        if (parsedVCard.n && parsedVCard.n.value) {
          const nameArray = parsedVCard.n.value;
          const firstName = nameArray[1] || '';
          const lastName = nameArray[0] || '';
          return `${firstName} ${lastName}`.trim();
        }
      }

      return 'Contacto';
    },

    /**
     * Extrae y formatea los números de teléfono de un vCard parseado
     */
    extractPhoneNumbers(parsedVCard) {
      if (!parsedVCard || !parsedVCard.tel) {
        return [];
      }

      const phones = Array.isArray(parsedVCard.tel)
        ? parsedVCard.tel
        : [parsedVCard.tel];

      return phones.map(tel => {
        const phoneNumber = tel.value || tel;
        const meta = tel.meta || {};

        // Determinar el tipo de teléfono
        let phoneType = this.$t(
          'CONVERSATION.SHARED_CONTACT_PHONE_TYPES.PHONE'
        );

        if (meta.type) {
          const types = Array.isArray(meta.type) ? meta.type : [meta.type];

          if (types.includes('CELL'))
            phoneType = this.$t(
              'CONVERSATION.SHARED_CONTACT_PHONE_TYPES.MOBILE'
            );
          else if (types.includes('HOME'))
            phoneType = this.$t('CONVERSATION.SHARED_CONTACT_PHONE_TYPES.HOME');
          else if (types.includes('WORK'))
            phoneType = this.$t('CONVERSATION.SHARED_CONTACT_PHONE_TYPES.WORK');
          else
            phoneType =
              types[0] ||
              this.$t('CONVERSATION.SHARED_CONTACT_PHONE_TYPES.PHONE');
        }

        return {
          number: this.formatPhoneNumber(phoneNumber),
          type: phoneType,
          raw: this.rawPhoneNumber(phoneNumber),
        };
      });
    },

    /**
     * Normaliza teléfonos de WhatsApp Cloud a [{ number, raw }] (acepta { phone }, { number } o string).
     * El `type` (CELL/HOME...) NO se propaga a propósito: el template solo pinta el número en TODOS los
     * canales, así que no hay pérdida de paridad con EvolutionAPI. El backend sí guarda type (forward-compat).
     */
    extractPhonesFromList(phones) {
      return (phones || [])
        .map(phone => {
          const phoneNumber =
            typeof phone === 'string' ? phone : phone.phone || phone.number;

          if (!phoneNumber) return null;

          return {
            number: this.formatPhoneNumber(phoneNumber),
            raw: this.rawPhoneNumber(phoneNumber),
          };
        })
        .filter(Boolean);
    },

    /**
     * Extrae solo los dígitos del número (versión "raw"). Centralizado para que el path de
     * vCard (extractPhoneNumbers) y el de teléfonos explícitos (extractPhonesFromList) no diverjan.
     */
    rawPhoneNumber(value) {
      return String(value || '').replace(/\D/g, '');
    },

    /**
     * Formatea el número de teléfono para mostrarlo de manera legible
     */
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return '';

      // Limpiar el número pero mantener + y espacios para legibilidad
      return phoneNumber.replace(/[^\d\s\+\-\(\)]/g, '').trim();
    },
  },
};
</script>

<style lang="scss" scoped>
.contact--share {
  display: flex;
  padding: var(--space-normal);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-slab);
  border-radius: var(--border-radius-normal-small);
  background: var(--conversa2-neutral-50-color);
  margin-bottom: var(--space-normal);
  width: 300px;

  .contact--container {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: var(--space-normal);
    width: 100%;

    .contact--data {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-smaller);
      color: var(--conversa2-neutral-800-color);
      font-family: 'Source Sans Pro';
      font-size: var(--font-size-small);
      font-style: normal;
      font-weight: var(--font-weight-bold);
      line-height: var(--space-two);
      width: calc(100% - 72px);

      p {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        margin: 0;
      }

      .contact--wrap {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-micro);
        align-self: stretch;

        .contact--type {
          color: var(--conversa2-neutral-600-color);

          font-family: 'Source Sans Pro';
          font-size: var(--font-size-mini);
          font-style: normal;
          font-weight: var(--font-weight-normal);
          line-height: var(--space-normal);
        }

        .contact--phone {
          color: var(--conversa2-neutral-800-color);

          font-family: 'Source Sans Pro';
          font-size: var(--font-size-small);
          font-style: normal;
          font-weight: var(--font-weight-normal);
          line-height: var(--space-two);
        }
      }
    }

    .contact--avatar {
      flex: 0 0 auto;
      height: 56px;
      width: 56px;

      .contact--avatar-container {
        border-radius: 50%;
        height: 100%;
        width: 100%;
        display: flex;
        line-height: 100%;
        align-items: center;
        justify-content: center;
        text-align: center;
        background-color: var(--conversa2-neutral-100-color);
        cursor: default;
      }
    }
  }
}
</style>
